// NIP-44 v2 decryption + encrypted blob parsing.
// Ported from ui.ts, converted from CDN imports to npm packages.

import { secp256k1 } from '@noble/curves/secp256k1.js';
import { gcm } from '@noble/ciphers/aes.js';
import { chacha20 } from '@noble/ciphers/chacha.js';

// ─── Hex helpers ───

export function bytesToHex(bytes: Uint8Array): string {
  let hex = '';
  for (const b of bytes) hex += b.toString(16).padStart(2, '0');
  return hex;
}

export function hexToBytes(hex: string): Uint8Array {
  const arr = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) arr[i / 2] = parseInt(hex.slice(i, i + 2), 16);
  return arr;
}

// ─── Web Crypto HMAC-SHA256 ───

async function webHmac(key: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
  const ck = await crypto.subtle.importKey(
    'raw',
    key as BufferSource,
    { name: 'HMAC', hash: { name: 'SHA-256' } },
    false,
    ['sign'],
  );
  return new Uint8Array(await crypto.subtle.sign('HMAC', ck, data as BufferSource));
}

// ─── NIP-44 conversation key derivation ───

export async function nip44ConvKey(
  privKeyHex: string,
  pubKeyHex: string,
): Promise<Uint8Array> {
  const compressed = new Uint8Array(33);
  compressed[0] = 0x02;
  compressed.set(hexToBytes(pubKeyHex), 1);
  const shared = secp256k1.getSharedSecret(hexToBytes(privKeyHex), compressed);
  const salt = new TextEncoder().encode('nip44-v2');
  return webHmac(salt, shared.subarray(1, 33));
}

// ─── NIP-44 v2 decrypt ───

export async function nip44Decrypt(
  payload: string,
  convKey: Uint8Array,
): Promise<string> {
  const binary = atob(payload);
  const data = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) data[i] = binary.charCodeAt(i);

  const nonce = data.subarray(1, 33);
  const ciphertext = data.subarray(33, data.length - 32);
  const mac = data.subarray(data.length - 32);

  // HKDF-like expansion (3 iterations for 76 bytes, ceil(76/32)=3)
  const n = Math.ceil(76 / 32);
  const okm = new Uint8Array(n * 32);
  let t: Uint8Array = new Uint8Array(0);
  for (let i = 0; i < n; i++) {
    const input = new Uint8Array(t.length + nonce.length + 1);
    input.set(t, 0);
    input.set(nonce, t.length);
    input[t.length + nonce.length] = i + 1;
    t = await webHmac(convKey, input);
    okm.set(t, i * 32);
  }

  const keys = okm.subarray(0, 76);
  const chachaKey = keys.subarray(0, 32);
  const chachaNonce = keys.subarray(32, 44);
  const macKey = keys.subarray(44, 76);

  // Verify MAC
  const aad = new Uint8Array(nonce.length + ciphertext.length);
  aad.set(nonce, 0);
  aad.set(ciphertext, nonce.length);
  const calcMac = await webHmac(macKey, aad);
  let diff = 0;
  for (let i = 0; i < 32; i++) diff |= calcMac[i] ^ mac[i];
  if (diff !== 0) throw new Error('NIP-44 MAC verification failed');

  // Decrypt with chacha20
  const padded = chacha20(chachaKey, chachaNonce, ciphertext);
  const len = (padded[0] << 8) | padded[1];
  return new TextDecoder().decode(padded.subarray(2, 2 + len));
}

// ─── Encrypted blob parsing (message content format) ───

export interface ParsedBlob {
  wrapper: string;
  ephPubkeyHex: string;
  nonce: Uint8Array;
  ciphertext: Uint8Array;
}

export function parseEncryptedBlob(blob: Uint8Array): ParsedBlob {
  if (blob[0] !== 0x02) throw new Error('Unsupported blob version');
  let offset = 1;
  const wrapperLen = (blob[offset] << 8) | blob[offset + 1];
  offset += 2;
  const wrapper = new TextDecoder().decode(blob.subarray(offset, offset + wrapperLen));
  offset += wrapperLen;
  const ephPubkeyHex = bytesToHex(blob.subarray(offset, offset + 32));
  offset += 32;
  const nonce = blob.subarray(offset, offset + 12);
  offset += 12;
  const ciphertext = blob.subarray(offset);
  return { wrapper, ephPubkeyHex, nonce, ciphertext };
}

// ─── Full message decryption (local nsec) ───

export async function decryptBlobWithNsec(
  rawBytes: Uint8Array,
  secretKeyHex: string,
): Promise<Uint8Array> {
  const { wrapper, ephPubkeyHex, nonce, ciphertext } = parseEncryptedBlob(rawBytes);
  const convKey = await nip44ConvKey(secretKeyHex, ephPubkeyHex);
  const contentKeyHex = await nip44Decrypt(wrapper, convKey);
  const contentKey = hexToBytes(contentKeyHex);
  return gcm(contentKey, nonce).decrypt(ciphertext);
}

// ─── Full message decryption (NIP-07 extension) ───

export async function decryptBlobWithExtension(
  rawBytes: Uint8Array,
): Promise<Uint8Array> {
  if (!window.nostr?.nip44?.decrypt) {
    throw new Error('NIP-44 decryption not available');
  }
  const { wrapper, ephPubkeyHex, nonce, ciphertext } = parseEncryptedBlob(rawBytes);
  const contentKeyHex = await window.nostr.nip44.decrypt(ephPubkeyHex, wrapper);
  const contentKey = hexToBytes(contentKeyHex);
  return gcm(contentKey, nonce).decrypt(ciphertext);
}

// ─── Tracker stripping for HTML email previews ───

export function stripTrackers(html: string): { cleaned: string; blocked: number } {
  let blocked = 0;
  let cleaned = html;

  cleaned = cleaned.replace(/<script[\s\S]*?<\/script>/gi, () => { blocked++; return ''; });
  cleaned = cleaned.replace(/<noscript[\s\S]*?<\/noscript>/gi, '');
  cleaned = cleaned.replace(/<link\b[^>]*>/gi, '');
  cleaned = cleaned.replace(/<meta\b[^>]*>/gi, '');

  cleaned = cleaned.replace(/\son\w+\s*=\s*"[^"]*"/gi, '');
  cleaned = cleaned.replace(/\son\w+\s*=\s*'[^']*'/gi, '');
  cleaned = cleaned.replace(/\son\w+\s*=\s*[^\s>]+/gi, '');

  cleaned = cleaned.replace(/<img\b[^>]*>/gi, (match) => {
    const lower = match.toLowerCase();
    const isTiny =
      /\b(width|height)\s*=\s*["']?1\b/.test(lower) ||
      /\b(width|height)\s*:\s*1px/.test(lower) ||
      /\b(width|height)\s*:\s*1\b/.test(lower);
    const isTrackerDomain =
      /src\s*=\s*["'][^"']*(pixel|track|beacon|open\.gif|email|spy|stat|analytic|1x1)/i.test(lower);
    if (isTiny || isTrackerDomain) { blocked++; return ''; }
    return match;
  });

  cleaned = cleaned.replace(/display\s*:\s*none/gi, 'display:none');
  cleaned = cleaned.replace(/<style[\s\S]*?<\/style>/gi, (match) => {
    const lower = match.toLowerCase();
    if (/opacity\s*:\s*0|visibility\s*:\s*hidden/.test(lower)) { blocked++; return ''; }
    return match;
  });

  cleaned = cleaned.replace(
    /background-image\s*:\s*url\(["']?https?:\/\/[^)]+\)/gi,
    () => { blocked++; return ''; },
  );

  cleaned = cleaned.replace(/href\s*=\s*["']javascript:[^"']*["']/gi, 'href="#"');
  cleaned = cleaned.replace(/href\s*=\s*["']data:text\/html[^"']*["']/gi, 'href="#"');
  cleaned = cleaned.replace(/<iframe\b[^>]*>[\s\S]*?<\/iframe>/gi, '');
  cleaned = cleaned.replace(/<object\b[^>]*>[\s\S]*?<\/object>/gi, '');
  cleaned = cleaned.replace(/<embed\b[^>]*>/gi, '');
  cleaned = cleaned.replace(/<base\b[^>]*>/gi, '');

  return { cleaned, blocked };
}
